import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { RelatedNotes } from "../components/RelatedNotes";
import { DueInput } from "../components/DueInput";
import { NextStepButton } from "../components/plan/TaskSteps";
import clsx from "clsx";
import { WifiOff, AlertTriangle, ArrowDown, ArrowUp, Ban, Bell, BellOff, BookOpen, Check, CheckCircle2, ChevronDown, CirclePlay, CornerDownRight, GripVertical, KanbanSquare, List, Hand, Link2, Lock, Pencil, Search, Star, Trash2, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, post, del, patch } from "../lib/api";
import { offerUndo } from "../components/UndoBar";
import DOMPurify from "dompurify";
import { useSession } from "../lib/session";
import { enqueueLog, isNetworkError, newClientRef, sendLog, sendOrQueue, useOutbox } from "../lib/offline";
import { isMustDo, thisMonday, useSpaces, type Item, type Person, type StatusDef } from "../lib/work";
import { Avatar, Card, Empty, ErrorNote, Field, fmtDate, Modal, PageHeader } from "../components/ui";
import { Due, ItemRow, PersonPicker, PriorityBadge, StatusPill, Thumbs, UploadButton } from "../components/work";
import { ExternalList, useExternal } from "./integrations";
import { QuickAdd } from "../components/QuickAdd";
import { LinkedWork, SendToAppDialog, SendToButton, useDispatchTargets } from "../components/SendToApp";
import { showNext } from "../components/DoneNext";
import { TaskFiles, type TaskFile } from "../components/TaskFiles";
import { RichEditor } from "../components/RichEditor";
import { LinkedTasks } from "../components/LinkedTasks";
import { TaskPicker } from "../components/TaskPicker";
import { Board } from "../components/Board";
import { AskFrom, AskPanel } from "../components/AskPanel";
import { HandoffBox } from "../components/assistant/Handoff";
import { CategoryList, CategoryPicker, useCategories } from "../components/Categories";
import { ChaseButton } from "../components/Chase";
import { REPEAT_PRESETS, repeatLabel, sameRule } from "../lib/repeat";

type View = "mine" | "forme" | "delegated" | "team" | "all" | "elsewhere";
/** One list of work; these are just filters on it. */
const VIEWS: Array<[View, string]> = [["mine", "Mine"], ["forme", "For me"], ["delegated", "Delegated"], ["team", "My teams"], ["all", "Everything"], ["elsewhere", "In other apps"]];

function MobileCategorySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const cats = useCategories();
  if (!cats.data?.items.length) return null;
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Category">
      <option value="">All categories</option>
      {cats.data.items.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.open ?? 0})</option>)}
      <option value="none">No category</option>
    </select>
  );
}

/** The task's name: click it (or the pencil) to rename; Enter or clicking away saves, Esc cancels. */
function EditableTitle({ title, canEdit, saving, onSave }: { title: string; canEdit: boolean; saving: boolean; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(title);
  useEffect(() => { if (!editing) setText(title); }, [title, editing]);
  const commit = () => {
    const t = text.trim();
    setEditing(false);
    if (t && t !== title) onSave(t.slice(0, 300));
    else setText(title);
  };
  const cls = "text-[1.625rem] font-bold leading-tight tracking-tight";
  if (!canEdit) return <h1 className={cls}>{title}</h1>;
  if (editing) {
    return (
      <textarea autoFocus rows={1} maxLength={300} aria-label="Task name" value={text}
        className={`${cls} w-full min-w-[min(40rem,80vw)] resize-none rounded-lg border border-brand-300 bg-[var(--panel)] px-2 py-1 outline-none`}
        onChange={(e) => setText(e.target.value.replace(/\n/g, " "))}
        onFocus={(e) => { e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; e.currentTarget.select(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setText(title); setEditing(false); } }}
        onBlur={commit} />
    );
  }
  return (
    <h1 className={`${cls} group cursor-text`} onClick={() => setEditing(true)} title="Click to rename">
      {saving ? text : title}
      <button className="muted ml-2 inline-flex align-middle opacity-40 hover:opacity-100 group-hover:opacity-80" aria-label="Rename task" onClick={(e) => { e.stopPropagation(); setEditing(true); }}><Pencil size={16} /></button>
    </h1>
  );
}

/** Notes on a task: the Word-style editor, saved as you type. */
/** Checklist step text with a pencil: click to edit in place, Enter saves, Esc cancels (Liam, 2026-10-31). */
function ChecklistText({ text, done, canEdit, onSave }: { text: string; done: boolean; canEdit: boolean; onSave: (text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  useEffect(() => { if (!editing) setDraft(text); }, [text, editing]);
  const save = () => { const t = draft.trim(); setEditing(false); if (t && t !== text) onSave(t); };
  if (editing) {
    return (
      <input
        className="input !min-h-0 !py-1 text-sm"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") { setDraft(text); setEditing(false); } }}
        aria-label="Edit step"
      />
    );
  }
  return (
    <span>
      <span className={clsx(done && "muted line-through")}>{text}</span>
      {canEdit && (
        <button type="button" className="muted ml-2 inline-flex align-middle opacity-40 hover:opacity-100" aria-label={`Edit ${text}`} onClick={() => setEditing(true)}><Pencil size={13} /></button>
      )}
    </span>
  );
}

/** Save the task into Knowledge: shared with one of your teams (the default), or kept to yourself. Opens it in Knowledge. */
function SaveAsNote({ itemId }: { itemId: string }) {
  const [open, setOpen] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const teams = useQuery({ queryKey: ["note-teams"], queryFn: () => api<{ items: Array<{ id: string; name: string }> }>("/api/notes/teams"), enabled: open });
  const team = teamId ?? teams.data?.items[0]?.id ?? "";
  const save = useMutation({
    mutationFn: () => post<{ noteId: string; duplicate: boolean }>(`/api/work/items/${itemId}/to-note`, { teamId: team || null }),
    onSuccess: (r) => { setOpen(false); window.location.href = `/knowledge#note-${r.noteId}`; },
  });
  return (
    <>
      <button className="btn" title="Save this task into Knowledge" onClick={() => setOpen(true)}><BookOpen size={16} /> Save to Knowledge</button>
      {open && (
        <Modal title="Save to Knowledge" onClose={() => setOpen(false)}
          footer={<><button className="btn" onClick={() => setOpen(false)}>Cancel</button><button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Save to Knowledge"}</button></>}>
          <p className="muted text-sm">Knowledge gets the task's title, details, notes and checklist, with a link back to the task. It's filed under "Knowledge / From tasks".</p>
          <Field label="Who can see it">
            <select className="input" value={team} onChange={(e) => setTeamId(e.target.value)}>
              {teams.data?.items.map((t) => <option key={t.id} value={t.id}>{t.name} (team knowledge)</option>)}
              <option value="">Only me</option>
            </select>
          </Field>
          <ErrorNote error={save.error} />
        </Modal>
      )}
    </>
  );
}

function TaskNotes({ itemId, title, html, canEdit }: { itemId: string; title: string; html: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [why, setWhy] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(html);
  const save = async () => {
    setState("saving");
    try {
      await patch(`/api/work/items/${itemId}`, { notesHtml: latest.current });
      setState("saved");
      void qc.invalidateQueries({ queryKey: ["item", itemId] });
    } catch (e) {
      const st = (e as { status?: number }).status;
      setWhy(st === 413 || /too large|too_big|413/i.test(String((e as Error).message)) ? "Too long to save in one go — split it into a second note or attach the document as a file"
        : st && st < 500 ? (e as Error).message : "Not saved — check your connection");
      setState("error");
    }
  };
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); void save(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!canEdit && !html) return null;
  return (
    <Card title="Notes" actions={<span className="muted text-xs">{state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state === "error" ? <span className="text-red-700">{why || "Not saved — check your connection"}</span> : ""}</span>}>
      {canEdit ? (
        <RichEditor value={html} title={title} placeholder="Notes, meeting minutes, specs, tables, pictures… (paste or drop pictures here)"
          onChange={(v) => { latest.current = v; setState("idle"); if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => { timer.current = null; void save(); }, 1200); }} />
      ) : <div className="rich-doc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />}
    </Card>
  );
}

/**
 * The Tasks list with sub-tasks: a main task shows "2/5 sub-tasks"; click it to open them underneath.
 * A sub-task whose main task is in the same list sits under it instead of appearing twice.
 */
function TaskTree({ items, showOwner }: { items: Item[]; showOwner?: boolean }) {
  const ids = new Set(items.map((i) => i.id));
  const inList = new Map<string, Item[]>();
  for (const i of items) if (i.parentId && ids.has(i.parentId)) inList.set(i.parentId, [...(inList.get(i.parentId) ?? []), i]);
  const top = items.filter((i) => !i.parentId || !ids.has(i.parentId));
  return <ul className="px-2">{top.map((i) => <TreeNode key={i.id} i={i} inList={inList} depth={0} showOwner={showOwner} />)}</ul>;
}

function TreeNode({ i, inList, depth, showOwner }: { i: Item; inList: Map<string, Item[]>; depth: number; showOwner?: boolean }) {
  const [open, setOpen] = useState(false);
  const kids = useQuery({
    queryKey: ["items", "children", i.id],
    queryFn: () => api<{ items: Item[] }>(`/api/work/items?parentId=${i.id}&state=open,in_progress,waiting,done,cancelled&limit=100`),
    enabled: open,
  });
  const rows = open ? (kids.data?.items ?? inList.get(i.id) ?? []) : [];
  // open first, then in the order they were added (refs count up), so a checklist reads top to bottom
  const sorted = [...rows].sort((a, b) => Number(["done", "cancelled"].includes(a.state)) - Number(["done", "cancelled"].includes(b.state))); // the API orders by the owner's sort, then age
  return (
    <>
      <ItemRow i={i} showOwner={showOwner} subOpen={open} onSubToggle={i.subtasks ? () => setOpen((x) => !x) : undefined} />
      {open && (
        <li className="ml-6 border-l-2 border-[var(--line)] pl-2">
          {kids.isLoading && !rows.length ? <p className="muted px-3 py-2 text-xs">Loading sub-tasks…</p> : (
            <ul>{sorted.map((c) => (depth < 3 ? <TreeNode key={c.id} i={c} inList={inList} depth={depth + 1} showOwner={showOwner} /> : <ItemRow key={c.id} i={c} showOwner={showOwner} />))}</ul>
          )}
        </li>
      )}
    </>
  );
}

export function WorkListPage() {
  const [view, setView] = useState<View>(() => {
    const v = new URLSearchParams(location.search).get("view");
    return VIEWS.some(([k]) => k === v) ? (v as View) : "mine";
  });
  const [q, setQ] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [closed, setClosed] = useState(false);
  const [category, setCategory] = useState(() => new URLSearchParams(location.search).get("category") ?? "");
  const [sendToApp, setSendToApp] = useState(false);
  // FEAT-01: list or board; the choice sticks per browser
  const [mode, setMode] = useState<"list" | "board">(() => {
    const m = new URLSearchParams(location.search).get("mode");
    if (m === "board" || m === "list") return m;
    try { return localStorage.getItem("one.tasks.mode") === "board" ? "board" : "list"; } catch { return "list"; }
  });
  useEffect(() => { try { localStorage.setItem("one.tasks.mode", mode); } catch { /* private window */ } }, [mode]);
  const dispatchTargets = useDispatchTargets();
  const spaces = useSpaces();
  const board = mode === "board" && view !== "elsewhere";
  // the board always has Done and Cancelled columns, so it fetches every state (newest closed first within the cap)
  const params = new URLSearchParams({ view, ...(q ? { q } : {}), ...(spaceId ? { spaceId } : {}), ...(board ? { state: "open,in_progress,waiting,done,cancelled", limit: "200" } : closed ? { state: "done,cancelled" } : {}), ...(category ? { category } : {}) });
  const items = useQuery({ queryKey: ["items", params.toString()], queryFn: () => api<{ items: Item[] }>(`/api/work/items?${params}`), enabled: view !== "elsewhere" });
  const external = useExternal("all");
  return (
    <div>
      <PageHeader title="Tasks" subtitle="Your own tasks, what others handed to you, what you've handed to others, and your teams' work — one list." />
      <div className="mb-4"><QuickAdd /></div>
      <div className="mb-4 max-w-full overflow-x-auto"><div className="tabs" role="tablist">
        {VIEWS.map(([v, label]) => (
          <button key={v} role="tab" aria-selected={view === v} className="tab" onClick={() => setView(v)}>{label}</button>
        ))}
      </div></div>
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search size={16} className="subtle absolute left-3 top-[11px]" />
          <input className="input !pl-9" placeholder="Search title or WRK-…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search work" />
        </div>
        <select className="input !w-auto" value={spaceId} onChange={(e) => setSpaceId(e.target.value)} aria-label="Space">
          <option value="">All spaces</option>
          {spaces.data?.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {!board && <label className="btn cursor-pointer font-medium"><input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} /> Show closed</label>}
        {view !== "elsewhere" && (
          <div className="tabs" role="tablist" aria-label="Layout">
            <button role="tab" aria-selected={mode === "list"} className="tab" onClick={() => setMode("list")} title="List"><List size={14} /> List</button>
            <button role="tab" aria-selected={mode === "board"} className="tab" onClick={() => setMode("board")} title="Board"><KanbanSquare size={14} /> Board</button>
          </div>
        )}
      </div>
      {view === "elsewhere" ? (
        <Card title="In FusionTask, Mythos Tasks and other apps" count={external.data?.items.length} actions={<span className="flex items-center gap-2">{!!dispatchTargets.data?.items.length && <button className="btn btn-sm" onClick={() => setSendToApp(true)}>Create in another app</button>}<span className="muted text-xs">Opens in the app</span></span>}>
          {external.data?.items.length ? <ExternalList items={external.data.items} /> : <Empty>Nothing reported by other apps yet.</Empty>}
          {sendToApp && <SendToAppDialog onClose={() => setSendToApp(false)} />}
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)]">
          <aside className="hidden lg:block"><Card><CategoryList value={category} onChange={setCategory} /></Card></aside>
          <div className="min-w-0">
            <div className="mb-2 lg:hidden"><MobileCategorySelect value={category} onChange={setCategory} /></div>
            {board ? (
              <>
                <ErrorNote error={items.error} />
                {items.isLoading ? <Empty>Loading…</Empty> : <Board items={items.data?.items ?? []} space={spaces.data?.items.find((s) => s.id === spaceId)} spaces={spaces.data?.items ?? []} />}
              </>
            ) : (
            <Card flush bodyClassName="!pt-2">
              <ErrorNote error={items.error} />
              {items.isLoading ? <Empty>Loading…</Empty> : items.data?.items.length ? <TaskTree items={items.data.items} showOwner={view === "forme"} /> : <Empty icon={<Check size={20} />}>Nothing here. You're all caught up.</Empty>}
            </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface Detail {
  item: Item;
  space: { id: string; key: string; name: string; statuses: StatusDef[]; verification: boolean };
  ownerName: string | null;
  category: { id: string; name: string; color: string } | null;
  assignments: Array<{ personId: string; role: string; status: string; name: string }>;
  checklist: Array<{ id: string; text: string; required: boolean; evidenceRequired: boolean; done: boolean; note: string | null; attachments: string[]; doneBy: string | null; doneAt: string | null }>;
  comments: Array<{ id: string; body: string; attachments: string[]; createdAt: string; authorName: string }>;
  logs: Array<{ id: string; logDate: string; recordedAt: string | null; outcome: string; note: string; attachments: string[]; createdAt: string; personName: string }>;
  attachments: TaskFile[];
  history: Array<{ id: string; at: string; kind: string; data: Record<string, unknown>; actorName: string | null }>;
  prerequisites: Array<{ id: string; ref: string; title: string; state: string; hard: boolean }>;
  dependents: Array<{ id: string; ref: string; title: string; state: string }>;
  children: Array<{ id: string; ref: string; title: string; state: string }>;
  related: Array<{ id: string; ref: string; title: string; state: string }>;
  parent: { id: string; ref: string; title: string; state: string } | null;
  following: boolean;
  blockers: Array<{ code: string; message: string }>;
  can: { update: boolean; assign: boolean; close: boolean; verify: boolean; claim: boolean; delete?: boolean };
}

const MENTION_RE = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/g;
const renderBody = (b: string) => b.split(MENTION_RE).map((part, i) => (i % 3 === 1 ? <strong key={i} className="text-brand-700 dark:text-brand-300">@{part}</strong> : i % 3 === 2 ? null : <span key={i}>{part}</span>));

/** Plain text with https:// links made clickable (event-created items carry a link back to the source app). */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https:\/\/[^\s<>"]+)/g);
  return <>{parts.map((p, i) => (i % 2 ? <a key={i} href={p.replace(/[).,;]+$/, "")} target="_blank" rel="noreferrer" className="text-brand-600 underline">{p}</a> : p))}</>;
}

function UpdateBox({ d, onDone }: { d: Detail; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [savedOffline, setSavedOffline] = useState(false);
  const outbox = useOutbox();
  const waiting = outbox.items.filter((x) => x.itemId === d.item.id);
  const input = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const submit = async (outcome: "continuing" | "done" | "blocked") => {
    setBusy(true);
    setError(null);
    setSavedOffline(false);
    const entry = {
      clientRef: newClientRef(),
      itemId: d.item.id,
      itemTitle: d.item.title,
      outcome,
      note,
      photos: photos.map((f) => ({ name: f.name || "photo.jpg", type: f.type || "image/jpeg", blob: f as Blob })),
      uploadedIds: [] as string[],
      recordedAt: new Date().toISOString(),
    };
    const keepOffline = async (uploadedIds: string[], remaining: typeof entry.photos) => {
      await enqueueLog({ ...entry, uploadedIds, photos: remaining });
      setSavedOffline(true);
      setNote("");
      setPhotos([]);
    };
    try {
      if (!navigator.onLine) {
        await keepOffline([], entry.photos);
        return;
      }
      let progress = { ids: [] as string[], remaining: entry.photos };
      try {
        await sendLog(entry, async (ids, remaining) => { progress = { ids, remaining }; });
      } catch (err) {
        if (isNetworkError(err)) {
          await keepOffline(progress.ids, progress.remaining);
          return;
        }
        throw err;
      }
      setNote("");
      setPhotos([]);
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const needPhoto = d.item.evidencePolicy === "photo_required";
  return (
    <Card title="Update">
      <div className="space-y-3">
        {!outbox.online && <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"><WifiOff size={14} className="mr-1 inline" /> No connection. Your update is saved on this phone and sent automatically when you're back online.</div>}
        {savedOffline && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Saved on this phone. It will be sent as soon as there's signal.</div>}
        {waiting.length > 0 && <div className="muted text-xs">{waiting.length} update{waiting.length > 1 ? "s" : ""} for this job waiting to send{waiting.some((w) => w.status === "failed") ? " — one could not be saved, see the banner at the top" : ""}.</div>}
        <textarea className="input min-h-[5rem]" placeholder="What did you do? Anything blocking you?" value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="flex flex-wrap items-center gap-2">
          <input ref={input} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={(e) => { const fs = Array.from(e.target.files ?? []); setPhotos((p) => [...p, ...fs].slice(0, 20)); e.target.value = ""; }} />
          <button type="button" className="btn" onClick={() => input.current?.click()}>{needPhoto ? "Add photo (required to finish)" : "Add photo"}</button>
          {previews.map((u, i) => (
            <span key={u} className="relative">
              <img src={u} alt="" className="line h-12 w-12 rounded-md border object-cover" />
              <button type="button" aria-label="Remove photo" className="absolute -right-1.5 -top-1.5 rounded-full bg-black/70 px-1 text-[10px] text-white" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}>✕</button>
            </span>
          ))}
        </div>
        <ErrorNote error={error} />
        <div className="grid grid-cols-3 gap-2">
          <button className="btn justify-center !py-3" disabled={busy} onClick={() => void submit("continuing")}><CirclePlay size={18} /> Continuing</button>
          <button className="btn justify-center !py-3 !border-emerald-600 !bg-emerald-600 text-white" disabled={busy} onClick={() => void submit("done")}><Check size={18} /> Done</button>
          <button className="btn justify-center !py-3 text-amber-700" disabled={busy} onClick={() => void submit("blocked")}><Ban size={18} /> Blocked</button>
        </div>
        {busy && <p className="muted text-xs">{photos.length ? "Uploading photos…" : "Saving…"}</p>}
      </div>
    </Card>
  );
}

function CommentBox({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const [body, setBody] = useState("");
  const [mentioning, setMentioning] = useState(false);
  const m = useMutation({ mutationFn: () => post(`/api/work/items/${itemId}/comments`, { body }), onSuccess: () => { setBody(""); onDone(); } });
  return (
    <div className="space-y-2">
      <textarea className="input min-h-[4rem]" placeholder="Write a comment… use @ to mention someone" value={body} onChange={(e) => { setBody(e.target.value); if (e.target.value.endsWith("@")) setMentioning(true); }} />
      {mentioning && (
        <PersonPicker value={[]} multiple={false} placeholder="Who do you want to mention?" onChange={(p) => { const person = p[0]; if (person) setBody((b) => `${b.replace(/@$/, "")}@[${person.name}](${person.id}) `); setMentioning(false); }} />
      )}
      <ErrorNote error={m.error} />
      <div className="flex gap-2">
        <button className="btn" type="button" onClick={() => setMentioning((x) => !x)}>@ Mention</button>
        <button className="btn btn-primary" disabled={!body.trim() || m.isPending} onClick={() => m.mutate()}>Comment</button>
      </div>
    </div>
  );
}

function AssignDialog({ d, onClose }: { d: Detail; onClose: () => void }) {
  const qc = useQueryClient();
  const toPeople = (role: string) => d.assignments.filter((a) => a.role === role).map((a) => ({ id: a.personId, name: a.name, email: null }));
  const [assignees, setAssignees] = useState<Person[]>(toPeople("assignee"));
  const [helpers, setHelpers] = useState<Person[]>(toPeople("helper"));
  const [reviewers, setReviewers] = useState<Person[]>(toPeople("reviewer"));
  const m = useMutation({
    mutationFn: () => post(`/api/work/items/${d.item.id}/assign`, { assigneeIds: assignees.map((p) => p.id), helperIds: helpers.map((p) => p.id), reviewerIds: reviewers.map((p) => p.id) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["item", d.item.id] }); onClose(); },
  });
  return (
    <Modal title="People on this" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={m.isPending} onClick={() => m.mutate()}>Save</button></>}>
      <ErrorNote error={m.error} />
      <Field label="Assigned to"><PersonPicker value={assignees} onChange={setAssignees} /></Field>
      <Field label="Helpers" hint="They're asked to confirm."><PersonPicker value={helpers} onChange={setHelpers} /></Field>
      {d.space.verification && <Field label="Signs off" hint="Who verifies the work when it's marked done."><PersonPicker value={reviewers} onChange={setReviewers} /></Field>}
    </Modal>
  );
}

export function ItemPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useSession().person?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["item", id], queryFn: () => api<Detail>(`/api/work/items/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["item", id] });
    void qc.invalidateQueries({ queryKey: ["items"] });
    void qc.invalidateQueries({ queryKey: ["my-day"] });
    void qc.invalidateQueries({ queryKey: ["inbox"] });
  };
  // FEAT-02: without signal, a status change, a checklist tick or a field edit is kept on the phone and shown at once
  const local = (fn: (d: Detail) => Detail) => qc.setQueryData<Detail>(["item", id], (d) => (d ? fn(d) : d));
  const status = useMutation({
    networkMode: "always", // runs offline too, so the change can be kept on the phone
    mutationFn: async (statusKey: string) => {
      const def = q.data?.space.statuses.find((s) => s.key === statusKey);
      const r = await sendOrQueue<{ state: string; pendingVerification: boolean; title: string }>({ method: "POST", path: `/api/work/items/${id}/status`, body: { statusKey }, label: `Move “${q.data?.item.title ?? "task"}” to ${def?.label ?? statusKey}`, itemId: id });
      if (r.queued) { local((d) => ({ ...d, item: { ...d.item, statusKey, state: def?.canonical ?? d.item.state } })); return null; }
      return r.result;
    },
    onSuccess: (r) => {
      if (!r) return;
      refresh();
      void qc.invalidateQueries({ queryKey: ["plan-today"] });
      if (r?.state === "done") showNext(id, r.title); // Done → next
    },
  });
  const edit = useMutation({
    networkMode: "always",
    mutationFn: async (b: Record<string, unknown>) => {
      // replayed later without the version check: the phone's edit is the newest the person made
      const r = await sendOrQueue({ method: "PATCH", path: `/api/work/items/${id}`, body: { ...b, version: q.data?.item.version }, queueBody: b, label: `Change ${Object.keys(b).join(", ")} on “${q.data?.item.title ?? "task"}”`, itemId: id });
      if (r.queued) { local((d) => ({ ...d, item: { ...d.item, ...(b as Partial<Item>) } })); return null; }
      return r.result;
    },
    onSuccess: (r) => { if (r !== null) refresh(); },
  });
  const claim = useMutation({ mutationFn: () => post(`/api/work/items/${id}/claim`, {}), onSuccess: refresh });
  const verify = useMutation({ mutationFn: (approve: boolean) => post(`/api/work/items/${id}/verify`, { approve, note: approve ? undefined : prompt("What needs fixing?") ?? undefined }), onSuccess: refresh });
  const follow = useMutation({ mutationFn: (on: boolean) => post(`/api/work/items/${id}/follow`, { on }), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => del(`/api/work/items/${id}`),
    onSuccess: () => {
      const it = q.data?.item;
      offerUndo({ id, label: it ? `${it.ref} · ${it.title}` : "the task" });
      void qc.invalidateQueries();
      void navigate({ to: "/work" });
    },
  });
  const checklist = useMutation({
    networkMode: "always",
    mutationFn: async (b: Record<string, unknown>) => {
      if (b.action !== "toggle" && b.action !== "rename") return post(`/api/work/items/${id}/checklist`, b);
      const step = q.data?.checklist.find((c) => c.id === b.checklistId);
      const r = await sendOrQueue({ method: "POST", path: `/api/work/items/${id}/checklist`, body: b, label: `${b.action === "toggle" ? (b.done ? "Tick" : "Untick") : "Rename"} “${step?.text ?? "step"}” on “${q.data?.item.title ?? "task"}”`, itemId: id });
      if (r.queued) {
        local((d) => ({ ...d, checklist: d.checklist.map((c) => (c.id !== b.checklistId ? c : b.action === "toggle" ? { ...c, done: !!b.done, doneBy: b.done ? "you" : null, doneAt: b.done ? new Date().toISOString() : null } : { ...c, text: String(b.text) })) }));
        return null;
      }
      return r.result;
    },
    onSuccess: (r) => { if (r !== null) refresh(); },
  });
  const dep = useMutation({ mutationFn: (prerequisiteId: string) => post(`/api/work/items/${id}/dependencies`, { prerequisiteId }), onSuccess: refresh });
  const undep = useMutation({ mutationFn: (pre: string) => del(`/api/work/items/${id}/dependencies/${pre}`), onSuccess: refresh });
  const [assigning, setAssigning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newCheck, setNewCheck] = useState("");
  const [ckDrag, setCkDrag] = useState<string | null>(null);
  const [ckOver, setCkOver] = useState<string | null>(null);
  const timeline = useMemo(() => {
    if (!q.data) return [];
    return [
      ...q.data.logs.map((l) => ({ at: l.createdAt, kind: "log" as const, l })),
      ...q.data.comments.map((c) => ({ at: c.createdAt, kind: "comment" as const, c })),
    ].sort((a, b) => a.at.localeCompare(b.at));
  }, [q.data]);

  if (q.isLoading) return <Empty>Loading…</Empty>;
  if (!q.data) return <ErrorNote error={q.error} />;
  const d = q.data;
  const i = d.item;
  const open = i.state !== "done" && i.state !== "cancelled";
  const isAssignee = d.assignments.some((a) => a.personId === me && (a.role === "assignee" || (a.role === "helper" && a.status !== "declined")));
  const starred = isMustDo(i);
  const errors = status.error ?? edit.error ?? claim.error ?? verify.error ?? checklist.error ?? dep.error ?? remove.error;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button className="btn btn-ghost btn-sm -ml-2" onClick={() => (history.length > 1 ? history.back() : navigate({ to: "/work" }))}>← Back</button>
        <span className="subtle">/</span>
        <span className="muted font-medium">{d.space.name}</span>
        <span className="subtle">/</span>
        <span className="font-semibold text-[var(--text-2)] num">{i.ref}</span>
        {i.personal ? <span className="chip"><Lock size={12} /> Private · only you{i.assigneeIds.length ? " and the people it's handed to" : ""}</span> : i.sensitivity !== "normal" && <span className="chip capitalize"><Lock size={12} /> {i.sensitivity}</span>}
        {i.decisionNeeded && <span className="chip text-amber-700">Decision needed</span>}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <EditableTitle title={i.title} canEdit={d.can.update} saving={edit.isPending} onSave={(title) => edit.mutate({ title })} />
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm">
            <StatusPill state={i.state} label={d.space.statuses.find((s) => s.key === i.statusKey)?.label} pending={i.pendingVerification} />
            <PriorityBadge p={i.priority} />
            <Due at={i.dueAt} />
            <CategoryPicker itemId={i.id} value={d.category} />
            {i.slaState === "breached" && <span className="badge bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"><AlertTriangle size={12} /> SLA MISSED</span>}
            {i.slaState === "at_risk" && <span className="badge bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">SLA AT RISK</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {d.can.update && open && (
            <button className={clsx("btn", starred && "!border-amber-300 !bg-amber-50")} title={starred ? "Take the star off" : "Must-do: star it and keep it at the top until done"} onClick={() => edit.mutate(starred ? { mustDo: false } : { mustDo: true, ...(i.focusWeek ? {} : { focusWeek: thisMonday() }) })}>
              <Star size={16} className={starred ? "fill-amber-400 text-amber-400" : ""} /> {starred ? "Must-do" : "Star"}
            </button>
          )}
          <button className="btn" onClick={() => follow.mutate(!d.following)}>{d.following ? <BellOff size={16} /> : <Bell size={16} />} {d.following ? "Unfollow" : "Follow"}</button>
          <SaveAsNote itemId={i.id} />
          {d.can.delete && (
            <button className="btn text-red-700" title="Delete this task" disabled={remove.isPending}
              onClick={() => confirm(`Delete ${i.ref} · ${i.title}?${d.children?.length ? `\n\nIts ${d.children.length} sub-task${d.children.length === 1 ? "" : "s"} will be deleted too.` : ""}\n\nYou can undo this straight after.`) && remove.mutate()}>
              <Trash2 size={16} /> Delete
            </button>
          )}
        </div>
      </div>

      <ErrorNote error={errors} />
      {d.blockers.length > 0 && open && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Before this can be finished:</strong>
          <ul className="mt-1 list-disc pl-5">{d.blockers.map((b, k) => <li key={k}>{b.message}</li>)}</ul>
        </div>
      )}
      {d.can.claim && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-100 bg-brand-50/70 px-4 py-3">
          <span className="flex items-center gap-2.5 text-sm font-medium text-[var(--text-2)]"><Users size={16} className="text-brand-600" /> This is in your team's pool. Take it?</span>
          <button className="btn btn-primary" onClick={() => claim.mutate()}><Hand size={16} /> Claim</button>
        </div>
      )}
      {d.can.verify && (
        <div className="panel flex flex-wrap items-center justify-between gap-2 border-violet-300 p-3">
          <span className="text-sm">Marked done — please check the work and sign off.</span>
          <div className="flex gap-2">
            <button className="btn" onClick={() => verify.mutate(false)}>Send back</button>
            <button className="btn btn-primary" onClick={() => verify.mutate(true)}><CheckCircle2 size={16} /> Sign off</button>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {open && d.can.update && isAssignee && !i.pendingVerification && <UpdateBox d={d} onDone={refresh} />}

          {i.description && (
            <Card title="Details"><div className="whitespace-pre-wrap break-words text-sm">{i.description.split(/\*\*(.+?)\*\*/g).map((s, k) => (k % 2 ? <strong key={k}>{s}</strong> : <Linkified key={k} text={s} />))}</div></Card>
          )}

          {i.source === "ask" && <AskPanel itemId={i.id} />}
          {i.source !== "ask" && <HandoffBox itemId={i.id} />}
          <TaskNotes itemId={i.id} title={i.title} html={i.notesHtml ?? ""} canEdit={d.can.update} />
          <LinkedTasks itemId={i.id} parent={d.parent} children={d.children} related={d.related} canEdit={d.can.update} />
          <TaskFiles itemId={i.id} files={d.attachments} canEdit={d.can.update} me={me} />

          <Card title={`Checklist${d.checklist.length ? ` (${d.checklist.filter((c) => c.done).length}/${d.checklist.length})` : ""}`}>
            <ul className="space-y-2">
              {d.checklist.map((c, n) => {
                const canOrder = d.can.update && open && d.checklist.length > 1;
                const ids = () => d.checklist.map((x) => x.id);
                const moveTo = (from: number, to: number) => { if (to < 0 || to >= d.checklist.length || from === to) return; const a = ids(); const [m] = a.splice(from, 1); a.splice(to, 0, m!); checklist.mutate({ action: "order", ids: a }); };
                return (
                <li key={c.id} className={clsx("group flex items-start gap-2 text-sm", ckOver === c.id && ckDrag && ckDrag !== c.id && "border-t-2 border-brand-500")}
                  draggable={canOrder} onDragStart={() => setCkDrag(c.id)} onDragOver={(e) => { if (canOrder && ckDrag) { e.preventDefault(); setCkOver(c.id); } }}
                  onDrop={(e) => { e.preventDefault(); if (ckDrag && ckDrag !== c.id) moveTo(ids().indexOf(ckDrag), n); setCkDrag(null); setCkOver(null); }} onDragEnd={() => { setCkDrag(null); setCkOver(null); }}>
                  {canOrder && <GripVertical size={13} className="muted mt-1.5 shrink-0 cursor-grab opacity-40 group-hover:opacity-100" aria-hidden />}
                  {canOrder && (
                    <span className="mt-0.5 flex shrink-0 flex-col opacity-40 group-hover:opacity-100">
                      <button type="button" aria-label="Move up" title="Move up" onClick={() => moveTo(n, n - 1)}><ArrowUp size={11} className="muted" /></button>
                      <button type="button" aria-label="Move down" title="Move down" onClick={() => moveTo(n, n + 1)}><ArrowDown size={11} className="muted" /></button>
                    </span>
                  )}
                  <input type="checkbox" className="mt-1 h-5 w-5" checked={c.done} disabled={!d.can.update || !open || (c.evidenceRequired && !c.done)} onChange={(e) => checklist.mutate({ action: "toggle", checklistId: c.id, done: e.target.checked })} aria-label={c.text} />
                  <span className="flex-1">
                    <ChecklistText text={c.text} done={c.done} canEdit={d.can.update && open} onSave={(text) => checklist.mutate({ action: "rename", checklistId: c.id, text })} />
                    {c.required && <span className="chip ml-2">required</span>}
                    {c.evidenceRequired && <span className="chip ml-1">photo</span>}
                    {c.doneBy && <span className="muted ml-2 text-xs">{c.doneBy} · {fmtDate(c.doneAt)}</span>}
                    <Thumbs ids={c.attachments} />
                  </span>
                  {c.evidenceRequired && !c.done && d.can.update && open && (
                    <UploadButton label="Photo & tick" onUploaded={(ids) => checklist.mutate({ action: "toggle", checklistId: c.id, done: true, attachments: ids.map((x) => x.id) })} />
                  )}
                  {d.can.update && open && !c.done && (
                    <button className="btn !min-h-0 !px-2 !py-1" aria-label="Turn into sub-task" title="Turn into a sub-task (own date, priority and assignee)" disabled={checklist.isPending}
                      onClick={() => checklist.mutate({ action: "to_subtask", checklistId: c.id })}><CornerDownRight size={14} /></button>
                  )}
                  {d.can.update && open && <button className="btn !min-h-0 !px-2 !py-1" aria-label="Remove" onClick={() => checklist.mutate({ action: "remove", checklistId: c.id })}><Trash2 size={14} /></button>}
                </li>
                );
              })}
            </ul>
            {d.can.update && open && (
              <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newCheck.trim()) { checklist.mutate({ action: "add", text: newCheck.trim() }); setNewCheck(""); } }}>
                <input className="input" placeholder="Add a checklist step" value={newCheck} onChange={(e) => setNewCheck(e.target.value)} />
                <button className="btn">Add</button>
              </form>
            )}
            {d.can.update && open && d.checklist.filter((c) => !c.done).length > 1 && (
              <button className="muted mt-2 text-xs hover:underline" disabled={checklist.isPending}
                onClick={() => { if (confirm(`Turn the ${d.checklist.filter((c) => !c.done).length} open steps into sub-tasks? They leave the checklist and appear under Linked tasks.`)) checklist.mutate({ action: "to_subtasks" }); }}>
                Turn all open steps into sub-tasks
              </button>
            )}
          </Card>

          <Card title="Activity">
            <ul className="space-y-3">
              {timeline.length === 0 && <li className="muted text-sm">No updates yet.</li>}
              {timeline.map((t) =>
                t.kind === "log" ? (
                  <li key={t.l.id} className="text-sm">
                    <span className="font-medium">{t.l.personName}</span>{" "}
                    <span className={clsx("chip", t.l.outcome === "done" ? "text-emerald-600" : t.l.outcome === "blocked" ? "text-amber-600" : "")}>{t.l.outcome}</span>
                    {t.l.recordedAt && <span className="muted text-xs" title="Saved on the phone without signal and sent later">recorded offline at {new Date(t.l.recordedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                    <span className="muted ml-2 text-xs">{fmtDate(t.l.createdAt)}</span>
                    {t.l.note && <p className="mt-1 whitespace-pre-wrap">“{t.l.note}”</p>}
                    <Thumbs ids={t.l.attachments} />
                  </li>
                ) : (
                  <li key={t.c.id} className="text-sm">
                    <span className="font-medium">{t.c.authorName}</span>
                    <span className="muted ml-2 text-xs">{fmtDate(t.c.createdAt)}</span>
                    <p className="mt-1 whitespace-pre-wrap">{renderBody(t.c.body)}</p>
                    <Thumbs ids={t.c.attachments} />
                  </li>
                ),
              )}
            </ul>
            <div className="line mt-4 border-t pt-4"><CommentBox itemId={i.id} onDone={refresh} /></div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Status">
            <div className="tabs w-full flex-wrap">
              {d.space.statuses.map((s) => (
                <button key={s.key} disabled={!d.can.update || status.isPending || i.statusKey === s.key} aria-selected={i.statusKey === s.key} onClick={() => status.mutate(s.key)} className="tab flex-1 justify-center disabled:cursor-default">{s.label}</button>
              ))}
            </div>
          </Card>
          <Card title="People" actions={<span className="flex items-start gap-2">{(i.ownerPersonId === me || i.createdBy === me) && !["done", "cancelled"].includes(i.state) && (i.assigneeIds.some((a) => a !== me) || i.waitingOn) && <ChaseButton item={i as never} compact />}{d.can.assign && <button className="btn btn-sm" onClick={() => setAssigning(true)}><Users size={14} /> Change</button>}</span>}>
            <dl className="space-y-3.5 text-sm">
              {i.source === "ask" && <AskFrom itemId={i.id} />}
              <div><dt className="section-label mb-1">{i.source === "ask" ? "Owner" : "Requested by / owner"}</dt><dd className="flex items-center gap-2 font-medium">{d.ownerName ? <><Avatar name={d.ownerName} size={24} />{d.ownerName}</> : "—"}</dd></div>
              {["assignee", "helper", "reviewer"].map((role) => {
                const ppl = d.assignments.filter((a) => a.role === role);
                if (!ppl.length && role !== "assignee") return null;
                return (
                  <div key={role}>
                    <dt className="section-label mb-1">{role === "assignee" ? "Assigned to" : role === "helper" ? "Helpers" : "Signs off"}</dt>
                    <dd className="space-y-1.5 font-medium">
                      {ppl.length ? ppl.map((a) => (
                        <div key={a.personId} className="flex items-center gap-2"><Avatar name={a.name} size={24} />{a.name}{a.status === "pending" && <span className="muted text-xs font-normal">(asked)</span>}{a.status === "declined" && <span className="text-xs font-normal text-red-600">(declined)</span>}</div>
                      )) : <span className="muted font-normal">{i.poolTeamId ? "Team pool — unclaimed" : "Nobody yet"}</span>}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </Card>
          <RelatedNotes itemId={i.id} canLink={!!d.can.update} />
          <Card title="Plan" actions={<SendToButton source={{ itemId: i.id }} />}>
            <div className="space-y-2 text-sm">
              {d.can.update && !["done", "cancelled"].includes(i.state) && (
                <NextStepButton id={i.id} title={i.title} current={i.nextAction} className="btn btn-sm w-full justify-center" onDone={() => refresh()} />
              )}
              <Field label="Do on" hint="The day you'll work on it (separate from the deadline).">
                <input className="input" type="date" disabled={!d.can.update} key={i.plannedDate ?? "none"} defaultValue={i.plannedDate ?? ""} onBlur={(e) => e.target.value !== (i.plannedDate ?? "") && edit.mutate({ plannedDate: e.target.value || null })} />
              </Field>
              <Field label="Next action">
                <input className="input" disabled={!d.can.update} key={i.nextAction ?? ""} defaultValue={i.nextAction ?? ""} placeholder="The very next step" onBlur={(e) => e.target.value !== (i.nextAction ?? "") && edit.mutate({ nextAction: e.target.value || null })} />
              </Field>
              <Field label="Follow up on">
                <input className="input" type="date" disabled={!d.can.update} defaultValue={i.followUpOn ?? ""} onBlur={(e) => e.target.value !== (i.followUpOn ?? "") && edit.mutate({ followUpOn: e.target.value || null })} />
              </Field>
              <Field label="Horizon">
                <select className="input" disabled={!d.can.update} value={i.horizon ?? ""} onChange={(e) => edit.mutate({ horizon: e.target.value || null })}>
                  <option value="">—</option><option value="next">Next</option><option value="later">Later</option><option value="someday">Someday</option>
                </select>
              </Field>
              <Field label="Repeats" hint={i.repeat ? "When you finish it, the next one is created automatically." : undefined}>
                <select className="input" disabled={!d.can.update} value={i.repeat ? (REPEAT_PRESETS.find(([, r]) => sameRule(r, i.repeat))?.[0] ?? "custom") : ""}
                  onChange={(e) => edit.mutate({ repeat: e.target.value ? REPEAT_PRESETS.find(([l]) => l === e.target.value)?.[1] ?? i.repeat : null })}>
                  <option value="">Doesn't repeat</option>
                  {REPEAT_PRESETS.map(([l]) => <option key={l} value={l}>{l}</option>)}
                  {i.repeat && !REPEAT_PRESETS.some(([, r]) => sameRule(r, i.repeat)) && <option value="custom">{repeatLabel(i.repeat)}</option>}
                </select>
              </Field>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!d.can.update} checked={!!i.decisionNeeded} onChange={(e) => edit.mutate({ decisionNeeded: e.target.checked })} /> Decision needed</label>
            </div>
            <div className="mt-3"><LinkedWork source={{ itemId: i.id }} /></div>
          </Card>
          <Card title="Dates">
            <div className="space-y-2 text-sm">
              <Field label="Due">
                <DueInput key={i.dueAt ?? "none"} value={i.dueAt} disabled={!d.can.update} onChange={(dueAt) => edit.mutate({ dueAt })} />
              </Field>
              <Field label="Priority">
                <select className="input" disabled={!d.can.update} value={i.priority} onChange={(e) => edit.mutate({ priority: e.target.value })}>
                  {[["P1", "Urgent"], ["P2", "High"], ["P3", "Normal"], ["P4", "Low"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              {i.slaDueAt && <p className="muted text-xs">SLA due {fmtDate(i.slaDueAt)}</p>}
              <p className="muted text-xs">Created {fmtDate(i.createdAt)}{i.completedAt ? ` · closed ${fmtDate(i.completedAt)}` : ""}</p>
            </div>
          </Card>
          <Card title="Depends on">
            {d.prerequisites.length === 0 && <p className="muted text-sm">Nothing.</p>}
            <ul className="space-y-1 text-sm">
              {d.prerequisites.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  {p.state === "done" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Link2 size={14} className="muted" />}
                  <Link to="/work/$id" params={{ id: p.id }} className="truncate hover:underline">{p.ref} {p.title}</Link>
                  {d.can.update && <button className="ml-auto" aria-label="Remove dependency" onClick={() => undep.mutate(p.id)}>×</button>}
                </li>
              ))}
            </ul>
            {d.dependents.length > 0 && <p className="muted mt-2 text-xs">Blocks: {d.dependents.map((x) => x.ref).join(", ")}</p>}
            {d.can.update && open && (
              <div className="mt-2">
                <TaskPicker placeholder="Add: search WRK-… or title" exclude={[i.id, ...d.prerequisites.map((x) => x.id)]} onPick={(x) => dep.mutate(x.id)} />
              </div>
            )}
          </Card>
          <Card title={<button className="flex items-center gap-1" onClick={() => setShowHistory((x) => !x)}>History <ChevronDown size={14} className={clsx(showHistory && "rotate-180")} /></button>}>
            {showHistory ? (
              <ul className="space-y-1 text-xs">
                {d.history.map((h) => <li key={h.id}><span className="muted">{fmtDate(h.at)}</span> · {h.actorName ?? "ONE"} · {h.kind === "step" ? <>step: {h.data.done ? <span className="line-through">{String(h.data.done)}</span> : null}{h.data.done ? " → " : ""}<b>{String(h.data.next)}</b>{h.data.date ? ` (${String(h.data.date)})` : ""}</> : h.kind}{h.kind === "status" ? ` → ${String(h.data.to)}` : ""}</li>)}
              </ul>
            ) : <p className="muted text-xs">{d.history.length} events</p>}
          </Card>
        </div>
      </div>
      {assigning && <AssignDialog d={d} onClose={() => setAssigning(false)} />}
    </div>
  );
}
